import { ReactNode, useCallback, useMemo, useState } from 'react';
import { contextFactory } from '@frontend/design-system';

export type HistoryFilter<Scope extends string = string> = {
  /** which widget/scope this belongs to */
  scope: Scope;
  /** the type within that scope (e.g. 'sentiment'|'category') */
  filterType: string;
  /** the actual key/id (e.g. 'SENTIMENT_POSITIVE','CATEGORY_BILLING') */
  key: string;
  /** label for the filter chip to display*/
  label: string;
};

export interface ContactHistoryStore {
  /** all currently‑applied filters */
  filters: HistoryFilter[];
  /** add or replace a filter in its scope */
  setFilter: (filter: HistoryFilter) => void;
  /** remove a single filter by matching scope, filterType & key */
  removeFilter: (filter: HistoryFilter) => void;
  /** replaces all filters in the scope: */
  replaceFilter: (filter: HistoryFilter) => void;
  /** clear _all_ filters for one scope */
  clearScope: (scope: string) => void;
  /** clear _every_ filter in every scope */
  clearAll: () => void;
}

const [ContactHistoryContext, useContactHistory] = contextFactory<ContactHistoryStore>(
  'useContactHistory must be used within a ContactHistoryProvider'
);

export const ContactHistoryProvider = ({ children }: { children: ReactNode }) => {
  const [filters, setFilters] = useState<HistoryFilter[]>([]);

  const setFilter = useCallback((filter: HistoryFilter) => {
    setFilters((current) => [
      ...current.filter(
        (f) => !(f.scope === filter.scope && f.filterType === filter.filterType && f.key === filter.key)
      ),
      filter,
    ]);
  }, []);

  const removeFilter = useCallback((filter: HistoryFilter) => {
    setFilters((current) =>
      current.filter((f) => !(f.scope === filter.scope && f.filterType === filter.filterType && f.key === filter.key))
    );
  }, []);

  const replaceFilter = useCallback(
    (filter: HistoryFilter) => setFilters((current) => [...current.filter((f) => f.scope !== filter.scope), filter]),
    []
  );

  const clearScope = useCallback((scope: string) => {
    setFilters((current) => current.filter((f) => f.scope !== scope));
  }, []);

  const clearAll = useCallback(() => {
    setFilters([]);
  }, []);

  const value = useMemo(() => ({ filters, setFilter, removeFilter, replaceFilter, clearScope, clearAll }), [filters]);

  return <ContactHistoryContext.Provider value={value}>{children}</ContactHistoryContext.Provider>;
};

export { useContactHistory };
